import { availabilityKeysPerDay } from '../constants/availabilityConstants'
import { AvailabilityDayFormFieldModel } from '../types/availability'
import { DaysOfWeek } from '../types/daysOfWeek'
import {
    RegistrationAvailabilityFormModel,
    RegistrationFormKeys,
    RegistrationFormModel,
} from '../types/registration-data'
import { RegistrationOpeningHoursModel } from '../types/__generated__/registration'
import dayjs from 'dayjs'

export const getTimeDifference = (timeOne: string, timeTwo: string): number => {
    const timeRegex = new RegExp(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)
    if (!timeRegex.test(timeOne) || !timeRegex.test(timeTwo)) return 0

    return dayjs(`2000-01-01 ${timeOne}`).diff(`2000-01-1 ${timeTwo}`, 'minute', true)
}

export const calculateTotalOpenMinutes = (formValues: AvailabilityDayFormFieldModel): number => {
    let totalMinutes = 0
    Object.entries(availabilityKeysPerDay).forEach((entry) => {
        const [key, value] = entry
        if (!formValues[key as keyof RegistrationFormModel]) return

        totalMinutes += Math.max(0, getTimeDifference(formValues[value[1]] as string, formValues[value[0]] as string))
        if (!formValues[RegistrationFormKeys.AVAILABLE_DURING_BREAK]) {
            totalMinutes += Math.max(
                0,
                getTimeDifference(formValues[value[3]] as string, formValues[value[2]] as string)
            )
        }
    })
    return totalMinutes
}

export const getDayFormFieldKeyByDayOfWeek = (dayOfWeek: DaysOfWeek): RegistrationFormKeys => {
    switch (dayOfWeek) {
        case DaysOfWeek.MONDAY:
            return RegistrationFormKeys.MONDAY
        case DaysOfWeek.TUESDAY:
            return RegistrationFormKeys.TUESDAY
        case DaysOfWeek.WEDNESDAY:
            return RegistrationFormKeys.WEDNESDAY
        case DaysOfWeek.THURSDAY:
            return RegistrationFormKeys.THURSDAY
        case DaysOfWeek.FRIDAY:
            return RegistrationFormKeys.FRIDAY
        case DaysOfWeek.SATURDAY:
            return RegistrationFormKeys.SATURDAY
        case DaysOfWeek.SUNDAY:
            return RegistrationFormKeys.SUNDAY
    }
}

export const getDayOfWeekByDayFormFieldKey = (dayFormFieldKey: RegistrationFormKeys): DaysOfWeek => {
    switch (dayFormFieldKey) {
        case RegistrationFormKeys.MONDAY:
        case RegistrationFormKeys.MONDAY_1_A:
        case RegistrationFormKeys.MONDAY_1_B:
        case RegistrationFormKeys.MONDAY_2_A:
        case RegistrationFormKeys.MONDAY_2_B:
            return DaysOfWeek.MONDAY
        case RegistrationFormKeys.TUESDAY:
        case RegistrationFormKeys.TUESDAY_1_A:
        case RegistrationFormKeys.TUESDAY_1_B:
        case RegistrationFormKeys.TUESDAY_2_A:
        case RegistrationFormKeys.TUESDAY_2_B:
            return DaysOfWeek.TUESDAY
        case RegistrationFormKeys.WEDNESDAY:
        case RegistrationFormKeys.WEDNESDAY_1_A:
        case RegistrationFormKeys.WEDNESDAY_1_B:
        case RegistrationFormKeys.WEDNESDAY_2_A:
        case RegistrationFormKeys.WEDNESDAY_2_B:
            return DaysOfWeek.WEDNESDAY
        case RegistrationFormKeys.THURSDAY:
        case RegistrationFormKeys.THURSDAY_1_A:
        case RegistrationFormKeys.THURSDAY_1_B:
        case RegistrationFormKeys.THURSDAY_2_A:
        case RegistrationFormKeys.THURSDAY_2_B:
            return DaysOfWeek.THURSDAY
        case RegistrationFormKeys.FRIDAY:
        case RegistrationFormKeys.FRIDAY_1_A:
        case RegistrationFormKeys.FRIDAY_1_B:
        case RegistrationFormKeys.FRIDAY_2_A:
        case RegistrationFormKeys.FRIDAY_2_B:
            return DaysOfWeek.FRIDAY
        case RegistrationFormKeys.SATURDAY:
        case RegistrationFormKeys.SATURDAY_1_A:
        case RegistrationFormKeys.SATURDAY_1_B:
        case RegistrationFormKeys.SATURDAY_2_A:
        case RegistrationFormKeys.SATURDAY_2_B:
            return DaysOfWeek.SATURDAY
        case RegistrationFormKeys.SUNDAY:
        case RegistrationFormKeys.SUNDAY_1_A:
        case RegistrationFormKeys.SUNDAY_1_B:
        case RegistrationFormKeys.SUNDAY_2_A:
        case RegistrationFormKeys.SUNDAY_2_B:
            return DaysOfWeek.SUNDAY
        default:
            return null
    }
}

export const getDayTranslation = (dayFormFieldKey: RegistrationFormKeys): string => {
    const dayOfWeek = getDayOfWeekByDayFormFieldKey(dayFormFieldKey)
    if (!dayOfWeek && dayOfWeek !== 0) return ''
    return dayjs().day(dayOfWeek).format('dddd')
}

const replaceEmptyStringWithNull = (value: string): string | null => (value === '' ? null : value)

export const mapAvailabilityForRegistrationPost = (
    registrationFormData: RegistrationFormModel
): RegistrationOpeningHoursModel[] => {
    const availability: RegistrationOpeningHoursModel[] = []
    Object.entries(availabilityKeysPerDay).forEach((entry) => {
        const [key, value] = entry
        if (!registrationFormData[key as keyof RegistrationFormModel]) return

        const hasAllTimesFilledIn: boolean =
            !!registrationFormData[value[0]] &&
            !!registrationFormData[value[1]] &&
            !!registrationFormData[value[2]] &&
            !!registrationFormData[value[3]]

        const start = registrationFormData[value[0]] as string
        const startBreak = hasAllTimesFilledIn ? (registrationFormData[value[1]] as string) : null
        const endBreak = hasAllTimesFilledIn ? (registrationFormData[value[2]] as string) : null
        const end = hasAllTimesFilledIn
            ? (registrationFormData[value[3]] as string)
            : (registrationFormData[value[1]] as string)

        availability.push({
            dayOfWeek: getDayOfWeekByDayFormFieldKey(key as RegistrationFormKeys),
            start: replaceEmptyStringWithNull(start),
            end: replaceEmptyStringWithNull(end),
            startBreak: replaceEmptyStringWithNull(startBreak),
            endBreak: replaceEmptyStringWithNull(endBreak),
        })
    })
    return availability
}

export const getAvailabilityLabelForSummary = (oneA: string, oneB: string, twoA?: string, twoB?: string): string => {
    let label = `${oneA} - ${oneB}`
    if (twoA && twoB) {
        label += ` / ${twoA} - ${twoB}`
    }
    return label
}

const getOpeningHoursForDay = (openingHours: RegistrationOpeningHoursModel[], dayOfWeek: DaysOfWeek) => {
    const times = openingHours?.find((openingHour) => openingHour.dayOfWeek === dayOfWeek)
    const hasBreak = times?.startBreak && times?.endBreak

    if (!times) {
        return {
            hasTimes: false,
            oneA: '',
            oneB: '',
            twoA: '',
            twoB: '',
        }
    }

    return {
        hasTimes: !!times,
        oneA: times.start as string,
        oneB: hasBreak ? (times.startBreak as string) ?? '' : (times.end as string) ?? '',
        twoA: hasBreak ? (times.endBreak as string) ?? '' : '',
        twoB: hasBreak ? (times.end as string) : '',
    }
}

export const mapOpeningHoursToFormData = (
    openingHours: RegistrationOpeningHoursModel[]
): Partial<RegistrationAvailabilityFormModel> => {
    const formModel: Partial<RegistrationAvailabilityFormModel> = {}

    const mondayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.MONDAY)
    formModel[RegistrationFormKeys.MONDAY] = mondayTimes.hasTimes
    formModel[RegistrationFormKeys.MONDAY_1_A] = mondayTimes.oneA
    formModel[RegistrationFormKeys.MONDAY_1_B] = mondayTimes.oneB
    formModel[RegistrationFormKeys.MONDAY_2_A] = mondayTimes.twoA
    formModel[RegistrationFormKeys.MONDAY_2_B] = mondayTimes.twoB

    const tuesdayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.TUESDAY)
    formModel[RegistrationFormKeys.TUESDAY] = tuesdayTimes.hasTimes
    formModel[RegistrationFormKeys.TUESDAY_1_A] = tuesdayTimes.oneA
    formModel[RegistrationFormKeys.TUESDAY_1_B] = tuesdayTimes.oneB
    formModel[RegistrationFormKeys.TUESDAY_2_A] = tuesdayTimes.twoA
    formModel[RegistrationFormKeys.TUESDAY_2_B] = tuesdayTimes.twoB

    const wednesdayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.WEDNESDAY)
    formModel[RegistrationFormKeys.WEDNESDAY] = wednesdayTimes.hasTimes
    formModel[RegistrationFormKeys.WEDNESDAY_1_A] = wednesdayTimes.oneA
    formModel[RegistrationFormKeys.WEDNESDAY_1_B] = wednesdayTimes.oneB
    formModel[RegistrationFormKeys.WEDNESDAY_2_A] = wednesdayTimes.twoA
    formModel[RegistrationFormKeys.WEDNESDAY_2_B] = wednesdayTimes.twoB

    const thursdayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.THURSDAY)
    formModel[RegistrationFormKeys.THURSDAY] = thursdayTimes.hasTimes
    formModel[RegistrationFormKeys.THURSDAY_1_A] = thursdayTimes.oneA
    formModel[RegistrationFormKeys.THURSDAY_1_B] = thursdayTimes.oneB
    formModel[RegistrationFormKeys.THURSDAY_2_A] = thursdayTimes.twoA
    formModel[RegistrationFormKeys.THURSDAY_2_B] = thursdayTimes.twoB

    const fridayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.FRIDAY)
    formModel[RegistrationFormKeys.FRIDAY] = fridayTimes.hasTimes
    formModel[RegistrationFormKeys.FRIDAY_1_A] = fridayTimes.oneA
    formModel[RegistrationFormKeys.FRIDAY_1_B] = fridayTimes.oneB
    formModel[RegistrationFormKeys.FRIDAY_2_A] = fridayTimes.twoA
    formModel[RegistrationFormKeys.FRIDAY_2_B] = fridayTimes.twoB

    const saturdayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.SATURDAY)
    formModel[RegistrationFormKeys.SATURDAY] = saturdayTimes.hasTimes
    formModel[RegistrationFormKeys.SATURDAY_1_A] = saturdayTimes.oneA
    formModel[RegistrationFormKeys.SATURDAY_1_B] = saturdayTimes.oneB
    formModel[RegistrationFormKeys.SATURDAY_2_A] = saturdayTimes.twoA
    formModel[RegistrationFormKeys.SATURDAY_2_B] = saturdayTimes.twoB

    const sundayTimes = getOpeningHoursForDay(openingHours, DaysOfWeek.SUNDAY)
    formModel[RegistrationFormKeys.SUNDAY] = sundayTimes.hasTimes
    formModel[RegistrationFormKeys.SUNDAY_1_A] = sundayTimes.oneA
    formModel[RegistrationFormKeys.SUNDAY_1_B] = sundayTimes.oneB
    formModel[RegistrationFormKeys.SUNDAY_2_A] = sundayTimes.twoA
    formModel[RegistrationFormKeys.SUNDAY_2_B] = sundayTimes.twoB

    formModel[RegistrationFormKeys.AVAILABLE_DURING_BREAK] = !openingHours?.some(
        (openingHour) => openingHour.startBreak && openingHour.endBreak
    )

    return formModel
}
